import { useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatCard, PackageBadge, StatusPill, Loader } from "@/components/ui-bits";
import { Search, ReceiptText, Loader2, ArrowUpRight, ArrowDownLeft, Wallet } from "lucide-react";

export default function AdminUserTransactions() {
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Enter a User ID or email");
    setBusy(true);
    try {
      const r = await API.get("/admin/user-transactions", { params: { q: q.trim() } });
      setD(r.data);
    } catch (e) { setD(null); toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="User Transactions" subtitle="Search a member by User ID (referral code) or email to view all of their transactions." testid="admin-usertx-header" />

      <div className="glass rounded-2xl p-3 mb-6 flex gap-2">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search className="h-4 w-4 text-slate-500" />
          <input data-testid="usertx-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Enter User ID (e.g. SO123ABC) or email..." className="w-full bg-transparent text-white outline-none py-2" />
        </div>
        <button data-testid="usertx-search-btn" onClick={search} disabled={busy}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-slate-900 font-semibold px-5 py-2 rounded-xl">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Search
        </button>
      </div>

      {busy && !d ? <Loader /> : !d ? (
        <div className="glass rounded-2xl p-12 text-center text-slate-500" data-testid="usertx-empty">
          Search a member to view their transactions.
        </div>
      ) : (
        <>
          <div className="glass rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="usertx-member">
            <div>
              <div className="text-white font-semibold">{d.member.name}</div>
              <div className="text-xs text-slate-500">{d.member.email} · {d.member.mobile}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-num text-cyan-300 text-sm">{d.member.referral_code}</span>
              <PackageBadge id={d.member.package_id} />
              <StatusPill status={d.member.status} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard testid="usertx-count" label="Transactions" value={d.count} icon={ReceiptText} accent="cyan" />
            <StatCard testid="usertx-credit" label="Total Credited" value={d.total_credit} money icon={ArrowDownLeft} accent="emerald" />
            <StatCard testid="usertx-main" label="Main Wallet" value={d.member.main_wallet} money icon={Wallet} accent="gold" />
          </div>

          <div className="glass rounded-2xl p-2 sm:p-4" data-testid="usertx-table">
            {d.transactions.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center">No transactions for this member.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                    <th className="py-3 px-3">Date</th>
                    <th className="py-3 px-3">Type</th>
                    <th className="py-3 px-3">Amount</th>
                    <th className="py-3 px-3">Fee</th>
                    <th className="py-3 px-3">Net</th>
                    <th className="py-3 px-3">To / Wallet</th>
                    <th className="py-3 px-3 text-right">Status</th>
                  </tr></thead>
                  <tbody>
                    {d.transactions.map((t) => {
                      const net = t.net || 0;
                      return (
                        <tr key={t.id} className="border-b border-white/5 last:border-0">
                          <td className="py-3 px-3 font-num text-slate-400 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                          <td className="py-3 px-3">
                            <span className="inline-flex items-center gap-1.5 text-white">
                              {net >= 0 ? <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-400" /> : <ArrowUpRight className="h-3.5 w-3.5 text-rose-400" />}
                              <span className="capitalize">{String(t.type || "").replace(/_/g, " ")}</span>
                            </span>
                          </td>
                          <td className="py-3 px-3 font-num text-white">{fmt(t.gross)}</td>
                          <td className="py-3 px-3 font-num text-rose-400">{t.fee ? fmt(t.fee) : "—"}</td>
                          <td className={`py-3 px-3 font-num ${net >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmt(net)}</td>
                          <td className="py-3 px-3 text-xs text-slate-400">{t.destination || "—"}</td>
                          <td className="py-3 px-3 text-right"><StatusPill status={t.status || "completed"} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
